import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Quote } from "lucide-react";
import ApplyCta from "./ApplyCta";
import mayaSchmidImg from "@/assets/testimonials/maya-schmid.jpg";
import carolHandalImg from "@/assets/testimonials/carol-handal.jpeg";
import carlosChapmanImg from "@/assets/testimonials/carlos-chapman.jpg";
import johanaHernandezImg from "@/assets/testimonials/johana-hernandez.jpeg";
import marlonAngelesImg from "@/assets/testimonials/marlon-angeles.webp";
import carolinaIsaacImg from "@/assets/testimonials/carolina-isaac.webp";
import silvanaRattiImg from "@/assets/testimonials/silvana-ratti.webp";
import jorgeVarelaImg from "@/assets/testimonials/jorge-leiva-varela.webp";
import safeZoneImg from "@/assets/testimonials/safe-zone.webp";

const testimonials = [{
  quote: "Es increíble contar con esta mentoría, me permitió tomar decisiones claves con más fuerza y mostrarme con autenticidad y seguridad.",
  name: "Maya Schmid",
  role: "Assistant Director · The Coca-Cola Company",
  location: "México 🇲🇽",
  image: mayaSchmidImg
}, {
  quote: "Gracias por expandir mi mirada, los elegí por tercera vez consecutiva porque me han dado el apoyo y acompañamiento realmente has hecho un gran cambio en mi vida y lo agradezco muchísimo.",
  name: "Carol Handal",
  role: "Empresaria",
  location: "Honduras 🇭🇳",
  image: carolHandalImg
}, {
  quote: "El proceso que llevamos adelante para mí fue súper muy valioso. Me llevo no solo herramientas, sino también una disciplina, una rutina para poder estructurar el proceso comunicacional de mi marca.",
  name: "Carlos Chapman",
  role: "Empresario",
  location: "México 🇲🇽",
  image: carlosChapmanImg
}, {
  quote: "Cada sesión fue un encuentro profundo y de mucho aprendizaje, me ayudó a desbloquear miedo y tener resultados que antes no imaginaba tener.",
  name: "Johana Hernandez",
  role: "Empresaria",
  location: "Venezuela 🇻🇪",
  image: johanaHernandezImg
}, {
  quote: "Recibí mucho más de lo que esperaba! Desde el día uno aprendí cómo desbloquear muros mentales que yo mismo había creado. Logré desarrollar una ruta clara para mi marca personal con herramientas para mejorar mi presencia digital.",
  name: "Marlon Angeles",
  role: "Empresario",
  location: "Perú 🇵🇪",
  image: marlonAngelesImg
}, {
  quote: "Grandes profesionales que supieron escuchar y hacer realidad mi sueño!!",
  name: "Carolina Issac",
  role: "Fotógrafa",
  location: "Argentina 🇦🇷",
  image: carolinaIsaacImg
}, {
  quote: "¡Tremendo! Fuimos escalando y descubriendo nuevas facetas, que uno tal vez no lo tenía como un plan dentro de la empresa, así que estuvo buenísimo este descubrimiento.",
  name: "Silvana Ratti",
  role: "Empresa Constructora",
  location: "Argentina 🇦🇷",
  image: silvanaRattiImg
}, {
  quote: "Este año con ustedes ha sido para mí muy, muy, pero muy hermoso, muy importante. Fue un descubrimiento. Cuando digo que nuestras posibilidades se expanden, realmente creo en eso.",
  name: "Claudia Ferrara",
  role: "Caminando con Propósito",
  location: "Argentina 🇦🇷"
}, {
  quote: "Gracias por todo! Es impresionante su trabajo y estamos muy felices de haberlos conocido!!!",
  name: "Jorge Leiva Varela",
  role: "Oralix",
  location: "Argentina 🇦🇷",
  image: jorgeVarelaImg
}, {
  quote: "Queremos agradecer al equipazo, sin este hermoso capital humano no hubiéramos llegado a lo que hoy somos. Los recomiendo siempre a todas aquellas personas que quieran tener Marcas con valores.",
  name: "Ale y Emi",
  role: "Safe Zone",
  location: "Argentina 🇦🇷",
  image: safeZoneImg
}];

const videoTestimonials = [{
  name: "Silvana Ratti",
  role: "Empresaria Real Estate",
  location: "Argentina",
  videoId: "XZzlQnD4Hx4",
  isShort: true
}, {
  name: "Alejandro y Emilia",
  role: "PASTUNGUI",
  location: "Argentina",
  videoId: "vPncvlA9Zyw",
  isShort: true
}, {
  name: "Sabrina Spinelli",
  role: "ÓPTICA SPINELLI",
  location: "Argentina",
  videoId: "01FW2_HmJjw",
  isShort: true
}, {
  name: "Sergio Mur",
  role: "DANEVA",
  location: "Argentina",
  videoId: "Y_UrBPTLRik",
  isShort: true
}];

const useAutoScroll = (direction: "left" | "right") => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let animationId: number;
    let scrollPos = direction === "left" ? 0 : el.scrollWidth / 2;
    const speed = direction === "left" ? 0.35 : -0.35;

    if (direction === "right") {
      el.scrollLeft = el.scrollWidth / 2;
    }

    const animate = () => {
      scrollPos += speed;
      const half = el.scrollWidth / 2;
      if (direction === "left" && scrollPos >= half) scrollPos = 0;
      if (direction === "right" && scrollPos <= 0) scrollPos = half;
      el.scrollLeft = scrollPos;
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);

    const handleMouseEnter = () => cancelAnimationFrame(animationId);
    const handleMouseLeave = () => {
      scrollPos = el.scrollLeft;
      animationId = requestAnimationFrame(animate);
    };

    el.addEventListener("mouseenter", handleMouseEnter);
    el.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      cancelAnimationFrame(animationId);
      el.removeEventListener("mouseenter", handleMouseEnter);
      el.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [direction]);

  return ref;
};

const TestimonialScrollRow = ({
  items,
  direction,
}: {
  items: typeof testimonials;
  direction: "left" | "right";
}) => {
  const scrollRef = useAutoScroll(direction);
  const allItems = [...items, ...items];

  return (
    <div
      ref={scrollRef}
      className="flex gap-4 overflow-hidden"
      style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
    >
      {allItems.map((t, index) => (
        <div
          key={`${t.name}-${index}`}
          className="flex-shrink-0 w-72 md:w-80 bg-white/10 backdrop-blur-sm rounded-xl p-4 md:p-5 border border-white/10"
        >
          <Quote className="w-5 h-5 text-primary/50 mb-2 shrink-0" />
          <p className="text-white/90 text-sm leading-relaxed mb-3">
            "{t.quote}"
          </p>
          <div className="flex items-center gap-3">
            {t.image && (
              <div className="w-9 h-9 rounded-full overflow-hidden border border-white/20 shrink-0">
                <img src={t.image} alt={t.name} className="w-full h-full object-cover object-top" />
              </div>
            )}
            <div className="min-w-0">
              <p className="text-white font-medium text-sm truncate">{t.name}</p>
              <p className="text-white/60 text-xs truncate">
                {t.role} · {t.location}
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const Testimonials = () => {
  return (
    <section id="testimonios" className="py-24 md:py-32 relative overflow-hidden bg-background">
      <div className="container px-4 relative z-10">
        {/* Video testimonials */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <span className="text-muted-foreground text-sm font-semibold tracking-[0.3em] uppercase mb-6 block">
            Testimonios
          </span>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-foreground">
            Historias de <span className="text-primary">Transformación</span>
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {videoTestimonials.map((video, index) => (
            <motion.div
              key={video.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <div className="bg-card rounded-2xl overflow-hidden border border-border hover:border-primary/50 transition-all duration-300 hover:shadow-lg">
                <div className={`relative ${video.isShort ? 'aspect-[9/16]' : 'aspect-video'}`}>
                  <iframe
                    src={`https://www.youtube-nocookie.com/embed/${video.videoId}`}
                    title={`Testimonio de ${video.name}`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full"
                    loading="lazy"
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-foreground">{video.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {video.role} · {video.location}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Written testimonials — ALL visible, blue bg */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="rounded-3xl py-8 md:py-10 overflow-hidden"
          style={{ background: "linear-gradient(135deg, hsl(216 38% 20%), hsl(216 50% 30%))" }}
        >
          <h3 className="font-display text-2xl md:text-3xl font-bold text-white text-center mb-8 px-6">
            Lo que dicen nuestros clientes
          </h3>

          <TestimonialScrollRow items={testimonials.slice(0, 5)} direction="left" />
          <div className="mt-5">
            <TestimonialScrollRow items={testimonials.slice(5)} direction="right" />
          </div>
        </motion.div>

        <ApplyCta />
      </div>
    </section>
  );
};

export default Testimonials;